import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Users, ArrowUpRight, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import ReferralGift from "@/components/ReferralGift";

const TEXTURE =
  "https://images.pexels.com/photos/30715673/pexels-photo-30715673.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Complete() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !user.completed) navigate("/dashboard");
  }, [user, navigate]);

  const enterCommunity = async () => {
    let url = "https://www.skool.com/oxytocin-house-of-learning/about";
    try {
      const { data } = await api.post("/community/accept");
      if (data?.url) url = data.url;
      toast.success("Your pass to the House of Learning is open.");
    } catch (e) {}
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="min-h-screen bg-cream">
      <SiteNav variant="dark" />

      <section className="relative velvet bg-purple-deep pt-[72px] overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={TEXTURE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-purple-deep/85" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 py-24 text-center">
          <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <img src="/api/htt/assets/swan-platinum.svg" alt="" className="w-24 mx-auto mb-8 opacity-90" />
          </motion.div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-gold-pale font-bold mb-5">Eight modules · Well travelled</p>
          <h1 className="font-heading text-white text-[clamp(2.2rem,6vw,4rem)] leading-[1.05] mb-6 text-balance">
            {user?.name ? `${user.name}, ` : ""}you answered.
          </h1>
          <p className="text-white/80 text-lg max-w-xl mx-auto">
            Eight modules ago, somebody spoke. You've walked the whole path — Perceive, Intervene, Redesign — and left with things you now do differently.
          </p>
          <p className="mt-6 text-[11px] uppercase tracking-[0.24em] text-gold-pale">Forward Ever, Backward Never</p>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-10 flex flex-col items-center gap-3">
            <button
              onClick={enterCommunity}
              className="group inline-flex items-center gap-2.5 bg-gold text-purple-deep font-bold rounded-full px-9 py-4 text-base hover:bg-gold-pale transition-colors shadow-[0_0_50px_rgba(212,175,55,0.4)]"
              data-testid="complete-hero-community-btn"
            >
              <Users className="w-5 h-5" /> Enter the House of Learning
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <p className="text-white/55 text-[13px]">One tap into your free community — colleagues walking the same path.</p>
          </motion.div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-2xl border-2 border-gold bg-white p-8 flex flex-col">
          <div className="w-12 h-12 rounded-xl bg-purple/10 flex items-center justify-center mb-5"><Award className="w-6 h-6 text-gold-dark" /></div>
          <h3 className="font-heading text-2xl text-purple mb-2">Your certificate</h3>
          <p className="text-[14px] text-ink/70 leading-relaxed flex-1">A CPD-aligned certificate of honesty, issued in your name — ready to view, print or keep.</p>
          <Link to="/certificate" className="mt-6 inline-flex items-center gap-2 bg-purple-deep text-cream font-semibold rounded-full px-6 py-3 hover:bg-purple transition-colors text-sm" data-testid="complete-certificate-btn">
            View certificate <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }} className="rounded-2xl border border-line bg-white p-8 flex flex-col">
          <div className="w-12 h-12 rounded-xl bg-purple/8 flex items-center justify-center mb-5"><Users className="w-6 h-6 text-purple-mid" /></div>
          <h3 className="font-heading text-2xl text-purple mb-2">The House of Learning</h3>
          <p className="text-[14px] text-ink/70 leading-relaxed flex-1">Our free community — conversation, weekly live sessions, and colleagues walking the same path. Your doors are open.</p>
          <button onClick={enterCommunity} className="mt-6 inline-flex items-center gap-2 border border-purple text-purple font-semibold rounded-full px-6 py-3 hover:bg-wash transition-colors text-sm" data-testid="complete-community-btn">
            Enter the community <ArrowUpRight className="w-4 h-4" />
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.16 }} className="rounded-2xl border border-line bg-cream p-8 flex flex-col">
          <div className="w-12 h-12 rounded-xl bg-purple/10 flex items-center justify-center mb-5 font-heading text-gold-dark text-xl">62</div>
          <h3 className="font-heading text-2xl text-purple mb-2">Phoenix Rising</h3>
          <p className="text-[14px] text-ink/70 leading-relaxed flex-1">The full 62-hour programme, built on everything you practised here. Your £39 counts in full — <b className="text-purple">£360 bridges you across.</b></p>
          <a href="mailto:edward@oxytocin-learning.com?subject=Phoenix%20Rising%20—%20bridging%20my%20founding%20place" className="mt-6 inline-flex items-center gap-2 border border-purple text-purple font-semibold rounded-full px-6 py-3 hover:bg-wash transition-colors text-sm" data-testid="complete-phoenix-btn">
            Cross the bridge <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
      </main>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-4">
        <ReferralGift variant="light" />
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 pb-16 pt-8 text-center">
        <Link to="/dashboard" className="text-purple-mid font-semibold hover:text-purple underline underline-offset-4 text-sm" data-testid="complete-back-btn">
          ← Back to my course
        </Link>
      </div>

      <SiteFooter />
    </div>
  );
}
